import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { downloadPredictionPDF } from '../utils/pdfGenerator';
import {
  User,
  SignOut,
  Plus,
  Warning,
  Flask,
  Heartbeat,
  Gauge,
  Barbell,
  Moon as MoonIcon,
  Wine,
  Cigarette,
  UserCircle,
  Question,
  CalendarBlank,
  Target,
  Pulse,
  Brain,
  ShieldCheck,
  FileText
} from '@phosphor-icons/react';

// Import refactored components
import {
  PredictionLoadingOverlay,
  DataPanel,
  MMIChart,
  HormonalBalancePanel,
  DisclaimerModal,
  WellnessHelpModal,
  HormoneEducationModal,
  WarningModal,
  AgeRestrictionModal,
  calculateWellnessScore,
  getWellnessColor,
  getHormoneStatus,
  validateHormones,
  parseEstimatedMenopause,
  hormoneRanges
} from '../components/dashboard';

// Import onboarding wizard
import { OnboardingWizard } from '../components/onboarding';

export default function Dashboard() {
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get user ID from context or try to decode from token
  const getUserId = () => {
    if (currentUser?.user_id) return currentUser.user_id;
    const token = localStorage.getItem('menopath_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.user_id || payload.sub;
      } catch (e) {
        return null;
      }
    }
    return null;
  };
  
  const userId = getUserId();
  
  // Data state
  const [hormones, setHormones] = useState(null);
  const [lifestyle, setLifestyle] = useState(null);
  const [symptoms, setSymptoms] = useState(null);
  const [prediction, setPrediction] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [predicting, setPredicting] = useState(false);
  const [estimatedMenopause, setEstimatedMenopause] = useState(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showAnalysisReminder, setShowAnalysisReminder] = useState(false);
  
  // Modal state
  const [ageRestrictionMessage, setAgeRestrictionMessage] = useState(null);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showWellnessHelp, setShowWellnessHelp] = useState(false);
  const [selectedHormoneInfo, setSelectedHormoneInfo] = useState(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(() => {
    return localStorage.getItem(`menopath_disclaimer_${userId}`) === 'accepted';
  });

  // Calculate wellness score
  const riskData = calculateWellnessScore(lifestyle, hormones, symptoms);

  useEffect(() => {
    if (userId) {
      const accepted = localStorage.getItem(`menopath_disclaimer_${userId}`) === 'accepted';
      setDisclaimerAccepted(accepted);
    }
  }, [userId]);

  useEffect(() => {
    // Load data if we have a token in localStorage
    // This runs on mount and when location changes
    const token = localStorage.getItem('menopath_token');
    if (token) {
      loadUserData();
    } else {
      // No token, not loading
      setLoading(false);
    }
    
    // Check if data was just updated (from DataEntry or Lifestyle pages)
    if (location.state?.dataUpdated) {
      setShowAnalysisReminder(true);
      // Clear the state so it doesn't show again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.key, location.pathname, location.state?.refresh, location.state?.dataUpdated]);
  
  // Force refresh when navigating back to dashboard
  useEffect(() => {
    const handleFocus = () => {
      const token = localStorage.getItem('menopath_token');
      if (token && location.pathname === '/dashboard') {
        loadUserData();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [location.pathname]);
  
  // Also reload when userId becomes available (auth context initializes)
  useEffect(() => {
    if (userId && !loading) {
      // Re-check onboarding status when userId is available
      const onboardingStatus = localStorage.getItem(`menopath_onboarding_${userId}`);
      const shouldShowOnboarding = onboardingStatus === 'needs_onboarding';
      const onboardingDone = onboardingStatus === 'completed' || onboardingStatus === 'in_progress';
      
      if (shouldShowOnboarding && !onboardingDone) {
        setShowOnboarding(true);
      }
    }
  }, [userId]);

  const loadUserData = async () => {
    setLoading(true);
    
    // Safety timeout - if loading takes more than 10 seconds, stop loading
    const timeoutId = setTimeout(() => {
      setLoading(false);
      console.error('Loading timeout - API calls took too long');
    }, 10000);
    
    try {
      const [hormonesRes, lifestyleRes, predictionRes] = await Promise.allSettled([
        api.getLatestHormonalData(),
        api.getLatestLifestyleData(),
        api.getLatestPrediction()
      ]);

      clearTimeout(timeoutId);

      let hasHormones = false;
      let hasLifestyle = false;

      if (hormonesRes.status === 'fulfilled' && hormonesRes.value.data) {
        const data = hormonesRes.value.data;
        setHormones({
          ...data,
          estradiol: data.estradiol_e2 || data.estradiol
        });
        hasHormones = true;
      }
      
      if (lifestyleRes.status === 'fulfilled' && lifestyleRes.value.data) {
        const data = lifestyleRes.value.data;
        setLifestyle(data);
        setSymptoms({
          hot_flashes_severity: data.hot_flashes_severity,
          cycle_regularity: data.cycle_regularity,
          mood_changes: data.mood_changes
        });
        hasLifestyle = true;
      }
      
      if (predictionRes.status === 'fulfilled' && predictionRes.value.data) {
        setPrediction(predictionRes.value.data);
        const parsedMenopause = parseEstimatedMenopause(predictionRes.value.data);
        setEstimatedMenopause(parsedMenopause);
      }

      // Check if onboarding is needed - use getUserId() for reliability
      const currentUserId = getUserId();
      const onboardingStatus = currentUserId ? localStorage.getItem(`menopath_onboarding_${currentUserId}`) : null;
      
      // Show onboarding ONLY if status is 'needs_onboarding' (set by SetupPassword page for new users)
      // Regular users who login should go directly to dashboard, even with no data
      const shouldShowOnboarding = onboardingStatus === 'needs_onboarding';
      
      // Don't show if completed or in_progress
      const onboardingDone = onboardingStatus === 'completed' || onboardingStatus === 'in_progress';
      
      if (shouldShowOnboarding && !onboardingDone) {
        setShowOnboarding(true);
      } else {
        setShowOnboarding(false);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    loadUserData(); // Reload data after onboarding
  };

  const handleResendVerification = async () => {
    if (!currentUser?.email) return;
    setResendingVerification(true);
    try {
      await api.resendVerification(currentUser.email);
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error('Failed to send verification email. Please try again.');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!prediction) {
      toast.error('No prediction data available. Run an analysis first.');
      return;
    }
    
    try {
      downloadPredictionPDF({
        user: currentUser,
        hormones,
        lifestyle,
        prediction,
        wellnessScore: riskData.score,
        estimatedMenopause
      });
      toast.success('PDF report downloaded!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  const handleGetPrediction = async () => {
    if (!disclaimerAccepted) {
      setShowDisclaimer(true);
      return;
    }
    
    if (lifestyle?.age && lifestyle.age < 35) {
      setAgeRestrictionMessage("Our prediction model is optimized for women aged 35 and above. While you can still track your hormone levels, predictions for those under 35 may be less reliable. Consider checking back as you approach your mid-30s for more accurate insights.");
      return;
    }
    
    if (lifestyle?.age && lifestyle.age > 58) {
      setAgeRestrictionMessage("Based on your age, you may be in the final stages of your menopausal transition or have already completed it. Our predictions are most accurate for women still in perimenopause. We're honored to have been part of your journey.");
      return;
    }

    const warnings = validateHormones(hormones);
    if (warnings.length > 0) {
      setValidationWarnings(warnings);
      setShowWarningModal(true);
      return;
    }
    
    await proceedWithPrediction();
  };

  const handleAcceptDisclaimer = () => {
    if (userId) {
      localStorage.setItem(`menopath_disclaimer_${userId}`, 'accepted');
    }
    setDisclaimerAccepted(true);
    setShowDisclaimer(false);
    handleGetPredictionAfterDisclaimer();
  };

  const handleDeclineDisclaimer = () => {
    setShowDisclaimer(false);
    toast.error('You must accept the disclaimer to use the prediction feature.');
  };

  const handleGetPredictionAfterDisclaimer = async () => {
    if (lifestyle?.age && lifestyle.age < 35) {
      setAgeRestrictionMessage("Our prediction model is optimized for women aged 35 and above. While you can still track your hormone levels, predictions for those under 35 may be less reliable. Consider checking back as you approach your mid-30s for more accurate insights.");
      return;
    }
    
    if (lifestyle?.age && lifestyle.age > 58) {
      setAgeRestrictionMessage("Based on your age, you may be in the final stages of your menopausal transition or have already completed it. Our predictions are most accurate for women still in perimenopause. We're honored to have been part of your journey.");
      return;
    }

    const warnings = validateHormones(hormones);
    if (warnings.length > 0) {
      setValidationWarnings(warnings);
      setShowWarningModal(true);
      return;
    }
    
    await proceedWithPrediction();
  };

  const proceedWithPrediction = async () => {
    setShowWarningModal(false);
    setPredicting(true);
    try {
      const response = await api.getPrediction();
      if (response.data) {
        setPrediction(response.data);
        const parsedMenopause = parseEstimatedMenopause(response.data);
        setEstimatedMenopause(parsedMenopause);
        toast.success('Analysis complete!');
      }
    } catch (error) {
      toast.error('Unable to generate prediction. Please ensure your data is complete.');
    } finally {
      setPredicting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#5B21B6] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#64748B]">Loading clinical data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      {/* Onboarding Wizard for new users */}
      {showOnboarding && (
        <OnboardingWizard onComplete={handleOnboardingComplete} />
      )}

      {/* Prediction Loading Overlay */}
      {predicting && <PredictionLoadingOverlay />}
      
      {/* Modals */}
      {showDisclaimer && (
        <DisclaimerModal onAccept={handleAcceptDisclaimer} onDecline={handleDeclineDisclaimer} />
      )}
      
      {ageRestrictionMessage && (
        <AgeRestrictionModal message={ageRestrictionMessage} onClose={() => setAgeRestrictionMessage(null)} />
      )}

      {showWarningModal && validationWarnings.length > 0 && (
        <WarningModal 
          warnings={validationWarnings} 
          onClose={() => setShowWarningModal(false)} 
          onProceed={proceedWithPrediction} 
        />
      )}

      {showWellnessHelp && (
        <WellnessHelpModal onClose={() => setShowWellnessHelp(false)} />
      )}

      {selectedHormoneInfo && (
        <HormoneEducationModal hormoneKey={selectedHormoneInfo} onClose={() => setSelectedHormoneInfo(null)} />
      )}

      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xl font-bold text-[#5B21B6]">MenoPath</Link>
            <span className="hidden sm:inline-flex items-center px-2 py-1 bg-[#EDE9FE] text-[#5B21B6] text-xs font-medium rounded-full">
              <Pulse className="w-3 h-3 mr-1" /> Clinical Dashboard
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/profile" className="hidden sm:block text-right hover:opacity-80 transition-opacity">
              <p className="text-sm font-medium text-[#0F172A]">{currentUser?.name}</p>
              <p className="text-xs text-[#64748B]">Patient ID: {currentUser?.user_id?.slice(-8)}</p>
            </Link>
            <Button variant="ghost" onClick={handleLogout} className="text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]">
              <SignOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Email Verification Banner */}
      {currentUser && !currentUser.email_verified && (
        <div className="bg-gradient-to-r from-[#FEF3C7] to-[#FDE68A] border-b border-[#F59E0B]/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#F59E0B]/20 flex items-center justify-center flex-shrink-0">
                <Warning className="w-4 h-4 text-[#D97706]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#92400E]">Please verify your email address</p>
                <p className="text-xs text-[#A16207]">Check your inbox for the verification link to access all features.</p>
              </div>
            </div>
            <Button
              onClick={handleResendVerification}
              disabled={resendingVerification}
              size="sm"
              className="bg-[#F59E0B] hover:bg-[#D97706] text-white text-xs px-4 flex-shrink-0"
            >
              {resendingVerification ? 'Sending...' : 'Resend Email'}
            </Button>
          </div>
        </div>
      )}

      {/* Analysis Reminder Banner */}
      {showAnalysisReminder && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-[#EDE9FE] to-[#FCE7F3] border-b border-[#5B21B6]/20"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#5B21B6]/20 flex items-center justify-center flex-shrink-0 animate-pulse">
                <Brain className="w-4 h-4 text-[#5B21B6]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#5B21B6]">Data updated successfully!</p>
                <p className="text-xs text-[#7C3AED]">Click "Run Analysis" to generate your new personalized prediction.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  setShowAnalysisReminder(false);
                  handleGetPrediction();
                }}
                size="sm"
                className="bg-gradient-to-r from-[#5B21B6] to-[#EC4899] hover:opacity-90 text-white text-xs px-4 flex-shrink-0"
                data-testid="run-analysis-reminder-btn"
              >
                <Brain className="w-3 h-3 mr-1" /> Run Analysis Now
              </Button>
              <button
                onClick={() => setShowAnalysisReminder(false)}
                className="text-[#5B21B6] hover:text-[#7C3AED] p-1"
              >
                ✕
              </button>
            </div>
          </div>
        </motion.div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Top Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl p-4 border border-[#E2E8F0]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-[#64748B]">PATIENT AGE</span>
              <CalendarBlank className="w-4 h-4 text-[#94A3B8]" />
            </div>
            <p className="text-2xl font-bold text-[#0F172A]">{lifestyle?.age || '—'}</p>
            <p className="text-xs text-[#64748B] mt-1">years old</p>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-xl p-4 border border-[#E2E8F0]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-[#64748B]">CLINICAL STAGE</span>
              <Pulse className="w-4 h-4 text-[#94A3B8]" />
            </div>
            <p className="text-lg font-bold text-[#5B21B6]">{prediction?.stage || 'Pending'}</p>
            <p className="text-xs text-[#64748B] mt-1">menopausal status</p>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-xl p-4 border border-[#E2E8F0]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-[#64748B]">EST. ONSET</span>
              <Target className="w-4 h-4 text-[#94A3B8]" />
            </div>
            <p className="text-2xl font-bold text-[#EC4899]">
              {estimatedMenopause 
                ? `${estimatedMenopause.min}-${estimatedMenopause.max}`
                : '—'}
            </p>
            <p className="text-xs text-[#64748B] mt-1">predicted age range</p>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-xl p-4 border border-[#E2E8F0]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                <span className="text-xs font-medium text-[#64748B]">WELLNESS SCORE</span>
                <button 
                  onClick={() => setShowWellnessHelp(true)}
                  className="w-4 h-4 rounded-full bg-[#E2E8F0] hover:bg-[#CBD5E1] flex items-center justify-center transition-colors"
                  data-testid="wellness-help-btn"
                >
                  <Question className="w-3 h-3 text-[#64748B]" weight="bold" />
                </button>
              </div>
              <ShieldCheck className="w-4 h-4 text-[#94A3B8]" />
            </div>
            <p className="text-2xl font-bold" style={{ color: getWellnessColor(riskData.score) }}>
              {riskData.score}
            </p>
            <p className="text-xs text-[#64748B] mt-1">out of 100</p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-4 space-y-6">
            {/* Biomarker Panel */}
            <DataPanel title="Biomarker Analysis" icon={Flask} color="#5B21B6">
              {hormones ? (
                <div className="space-y-3">
                  {Object.entries(hormoneRanges).map(([key, range]) => {
                    const value = hormones[key];
                    const status = getHormoneStatus(key, value);
                    return (
                      <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
                        <div>
                          <p className="text-xs text-[#64748B]">{range.fullName}</p>
                          <p className="font-semibold text-[#0F172A]">
                            {value ? `${value} ${range.unit}` : '—'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: status.color }} />
                          <span className="text-xs font-medium" style={{ color: status.color }}>
                            {status.status === 'normal' ? 'Normal' : status.status === 'low' ? 'Low' : status.status === 'high' ? 'Elevated' : '—'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <Link to="/data-entry">
                    <Button variant="outline" className="w-full mt-2 border-dashed border-[#CBD5E1] text-[#64748B] hover:border-[#5B21B6] hover:text-[#5B21B6]">
                      <Plus className="w-4 h-4 mr-2" /> Update Lab Results
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Flask className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" weight="duotone" />
                  <p className="text-[#64748B] text-sm mb-4">No biomarker data available</p>
                  <Link to="/data-entry">
                    <Button className="bg-[#5B21B6] hover:bg-[#4C1D95] rounded-lg">
                      <Plus className="w-4 h-4 mr-2" /> Add Lab Results
                    </Button>
                  </Link>
                </div>
              )}
            </DataPanel>

            {/* Lifestyle Modifiers */}
            <DataPanel title="Lifestyle Modifiers" icon={Heartbeat} color="#10B981">
              {lifestyle ? (
                <div className="space-y-3">
                  {lifestyle.ethnicity && (
                    <div className="p-3 rounded-lg bg-[#F5F3FF] border border-[#DDD6FE]">
                      <div className="flex items-center gap-2 mb-1">
                        <UserCircle className="w-4 h-4 text-[#7C3AED]" />
                        <span className="text-xs text-[#7C3AED] font-medium">Ethnicity</span>
                      </div>
                      <p className="text-sm font-semibold text-[#0F172A] capitalize">
                        {lifestyle.ethnicity.replace(/_/g, ' ')}
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-[#F0FDF4] border border-[#BBF7D0]">
                      <div className="flex items-center gap-2 mb-1">
                        <Barbell className="w-4 h-4 text-[#16A34A]" />
                        <span className="text-xs text-[#16A34A] font-medium">Exercise</span>
                      </div>
                      <p className="text-sm font-semibold text-[#0F172A] capitalize">
                        {lifestyle.exercise_frequency || '—'}
                      </p>
                    </div>
                    
                    <div className="p-3 rounded-lg bg-[#EFF6FF] border border-[#BFDBFE]">
                      <div className="flex items-center gap-2 mb-1">
                        <MoonIcon className="w-4 h-4 text-[#2563EB]" />
                        <span className="text-xs text-[#2563EB] font-medium">Sleep</span>
                      </div>
                      <p className="text-sm font-semibold text-[#0F172A] capitalize">
                        {lifestyle.sleep_quality || '—'}
                      </p>
                    </div>
                    
                    <div className={`p-3 rounded-lg ${lifestyle.smoking_status === 'never' ? 'bg-[#F0FDF4] border-[#BBF7D0]' : 'bg-[#FEF2F2] border-[#FECACA]'} border`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Cigarette className={`w-4 h-4 ${lifestyle.smoking_status === 'never' ? 'text-[#16A34A]' : 'text-[#DC2626]'}`} />
                        <span className={`text-xs font-medium ${lifestyle.smoking_status === 'never' ? 'text-[#16A34A]' : 'text-[#DC2626]'}`}>Smoking</span>
                      </div>
                      <p className="text-sm font-semibold text-[#0F172A] capitalize">
                        {lifestyle.smoking_status === 'never' ? 'Non-smoker' : lifestyle.smoking_status === 'former' ? 'Former' : 'Current'}
                      </p>
                    </div>
                    
                    <div className={`p-3 rounded-lg ${lifestyle.alcohol_consumption === 'never' || lifestyle.alcohol_consumption === 'rarely' ? 'bg-[#F0FDF4] border-[#BBF7D0]' : 'bg-[#FFFBEB] border-[#FDE68A]'} border`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Wine className={`w-4 h-4 ${lifestyle.alcohol_consumption === 'never' || lifestyle.alcohol_consumption === 'rarely' ? 'text-[#16A34A]' : 'text-[#F59E0B]'}`} />
                        <span className={`text-xs font-medium ${lifestyle.alcohol_consumption === 'never' || lifestyle.alcohol_consumption === 'rarely' ? 'text-[#16A34A]' : 'text-[#F59E0B]'}`}>Alcohol</span>
                      </div>
                      <p className="text-sm font-semibold text-[#0F172A] capitalize">
                        {lifestyle.alcohol_consumption || '—'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-[#64748B] text-sm mb-4">Complete your lifestyle assessment</p>
                  <Link to="/lifestyle">
                    <Button variant="outline" className="border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white">
                      Add Lifestyle Data
                    </Button>
                  </Link>
                </div>
              )}
              {lifestyle && (
                <Link to="/lifestyle">
                  <Button variant="outline" className="w-full mt-3 border-dashed border-[#CBD5E1] text-[#64748B]">
                    <Plus className="w-4 h-4 mr-2" /> Update Lifestyle
                  </Button>
                </Link>
              )}
            </DataPanel>

            {/* Risk Factors */}
            <DataPanel title="Risk Factor Analysis" icon={Gauge} color="#F59E0B">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#64748B]">Overall Wellness Score</span>
                  <span className="text-lg font-bold" style={{ color: getWellnessColor(riskData.score) }}>
                    {riskData.score}/100
                  </span>
                </div>
                <div className="h-3 bg-[#E2E8F0] rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${riskData.score}%` }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: getWellnessColor(riskData.score) }}
                  />
                </div>
              </div>
              
              {riskData.factors.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-[#64748B] font-medium mb-2">Contributing Factors:</p>
                  {riskData.factors.map((factor, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-[#64748B]">{factor.name}</span>
                      <span className="font-medium" style={{ color: factor.color }}>
                        {factor.impact > 0 ? '+' : ''}{factor.impact}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </DataPanel>
          </div>

          {/* Right Column - Main Charts */}
          <div className="lg:col-span-8 space-y-6">
            {/* MMI Timeline Chart */}
            <MMIChart
              estimatedMenopause={estimatedMenopause}
              prediction={prediction}
              hormones={hormones}
              lifestyle={lifestyle}
              predicting={predicting}
              onGetPrediction={handleGetPrediction}
              onDownloadPDF={handleDownloadPDF}
            />

            {/* Hormone Profile Visualization */}
            {hormones && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <HormonalBalancePanel 
                  hormones={hormones} 
                  onSelectHormone={setSelectedHormoneInfo}
                />

                <DataPanel title="AI Clinical Summary" icon={Brain} color="#EC4899">
                  {prediction ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-gradient-to-br from-[#FDF4FF] to-[#FAF5FF] rounded-xl border border-[#E9D5FF]">
                        <p className="text-sm text-[#6B21A8] font-medium mb-2">Primary Assessment</p>
                        <p className="text-sm text-[#64748B] leading-relaxed">
                          {prediction.reasoning || 'Based on your biomarker profile and lifestyle factors, our AI model has generated personalized insights about your menopause timeline.'}
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                          <p className="text-xs text-[#64748B]">Stage</p>
                          <p className="text-sm font-semibold text-[#0F172A]">{prediction.stage}</p>
                        </div>
                        <div className="p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                          <p className="text-xs text-[#64748B]">Confidence</p>
                          <p className="text-sm font-semibold text-[#5B21B6]">{prediction.confidence}</p>
                        </div>
                      </div>
                      
                      {prediction.recommendations && (
                        <div className="p-3 bg-[#F0FDF4] rounded-lg border border-[#BBF7D0]">
                          <p className="text-xs text-[#16A34A] font-medium mb-1">Recommendation</p>
                          <p className="text-sm text-[#64748B]">{prediction.recommendations}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Brain className="w-12 h-12 text-[#E2E8F0] mx-auto mb-3" weight="duotone" />
                      <p className="text-sm text-[#64748B]">Run analysis to generate AI insights</p>
                    </div>
                  )}
                </DataPanel>
              </div>
            )}

            {/* Clinical Notes */}
            <DataPanel title="Clinical Reference Guide" icon={FileText} color="#64748B">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                  <h4 className="font-semibold text-[#0F172A] mb-2">FSH Interpretation</h4>
                  <p className="text-xs text-[#64748B]">
                    Elevated FSH ({'>'}25 mIU/mL) consistently indicates declining ovarian function. Values fluctuate during perimenopause.
                  </p>
                </div>
                <div className="p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                  <h4 className="font-semibold text-[#0F172A] mb-2">AMH Significance</h4>
                  <p className="text-xs text-[#64748B]">
                    AMH reflects ovarian reserve. Values &lt;1.0 ng/mL suggest diminished reserve; undetectable AMH indicates imminent menopause.
                  </p>
                </div>
                <div className="p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                  <h4 className="font-semibold text-[#0F172A] mb-2">Disclaimer</h4>
                  <p className="text-xs text-[#64748B]">
                    This analysis is for informational purposes only. Consult your healthcare provider for medical decisions.
                  </p>
                </div>
              </div>
            </DataPanel>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E2E8F0] bg-white mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#64748B]">
            <p>© 2026 MenoPath. For informational purposes only.</p>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-[#5B21B6]">Privacy</Link>
              <Link to="/terms" className="hover:text-[#5B21B6]">Terms</Link>
              <Link to="/faq" className="hover:text-[#5B21B6]">FAQ</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
